import { GUIDES_HUB_PATH, GUIDE_ARTICLE_PATHS } from './guides';
import type { MarketingLocale } from './site';

/**
 * Bilingual URL scheme for the marketing site.
 *
 * English lives at the plain path (/faq); Arabic lives at the same path under
 * /ar (/ar/faq). proxy.ts rewrites /ar/* to the plain route and sets
 * MARKETING_LOCALE_HEADER, which the i18n server instance honours above the
 * `lang` cookie, so each URL always renders one language. That is what lets
 * search engines index the Arabic pages: before this, Arabic was cookie-only
 * and crawlers (which send no cookie) only ever saw English.
 *
 * Only paths listed here get an Arabic twin; app routes are never rewritten.
 */
export const MARKETING_PATHS: readonly string[] = [
  '/',
  '/customers',
  '/faq',
  '/padel-software',
  '/pilates-studio-software',
  '/membership-club-software',
  '/enterprise',
  '/academy-software',
  '/gym-software',
  '/compare/mindbody',
  '/compare/glofox',
  '/compare/playtomic',
  '/booking-software/saudi-arabia',
  '/booking-software/uae',
  '/booking-software/jordan',
  '/best/padel-booking-software-saudi-arabia',
  '/best/studio-management-software-saudi-arabia',
  '/best/gym-management-software-saudi-arabia',
  '/guides/marketplace-vs-own-booking-app',
  GUIDES_HUB_PATH,
  ...GUIDE_ARTICLE_PATHS,
];

export const ARABIC_PREFIX = '/ar';

/**
 * Internal route that renders the apex homepage. proxy.ts rewrites '/' (and
 * '/ar') to it on non-tenant hosts; it is never linked or indexed directly.
 */
export const MARKETING_HOME_ROUTE = '/site-home';

/** Request header set by proxy.ts on /ar/* requests. */
export const MARKETING_LOCALE_HEADER = 'x-marketing-locale';

const MARKETING_PATH_SET: ReadonlySet<string> = new Set<string>(MARKETING_PATHS);

export function localizedMarketingPath(path: string, locale: MarketingLocale): string {
  if (locale === 'en') {
    return path;
  }

  return path === '/' ? ARABIC_PREFIX : `${ARABIC_PREFIX}${path}`;
}

/**
 * For an incoming pathname, returns the plain marketing route it should be
 * rewritten to when it is an Arabic URL (/ar or /ar/<marketing path>), or
 * null when it is not one.
 */
export function resolveArabicMarketingPath(pathname: string): string | null {
  if (pathname === ARABIC_PREFIX || pathname === `${ARABIC_PREFIX}/`) {
    return '/';
  }

  if (!pathname.startsWith(`${ARABIC_PREFIX}/`)) {
    return null;
  }

  const plain = pathname.slice(ARABIC_PREFIX.length).replace(/\/$/, '');

  return MARKETING_PATH_SET.has(plain) ? plain : null;
}

/** Plain path for a browser pathname that may carry the /ar prefix. */
export function stripArabicPrefix(pathname: string): string {
  return resolveArabicMarketingPath(pathname) ?? pathname;
}

/**
 * Localizes an internal marketing href ('/faq', '/#platform', '/customers#x')
 * for the given language. Non-marketing hrefs (app routes, external URLs,
 * in-page '#anchors') are returned unchanged.
 */
export function localizedHref(href: string, locale: MarketingLocale): string {
  if (locale === 'en' || !href.startsWith('/')) {
    return href;
  }

  const hashIndex = href.indexOf('#');
  const path = hashIndex === -1 ? href : href.slice(0, hashIndex) || '/';
  const hash = hashIndex === -1 ? '' : href.slice(hashIndex);

  if (!MARKETING_PATH_SET.has(path)) {
    return href;
  }

  return `${localizedMarketingPath(path, locale)}${hash}`;
}

export function isBilingualMarketingPath(path: string): boolean {
  return MARKETING_PATH_SET.has(path);
}
